/**
 * useCalcConfig.js
 *
 * Fetches calculator configuration from a published Google Sheet (CSV format).
 * Falls back to window.TricelConfig.calcConfig (WordPress admin settings) if
 * the sheet URL is not configured or the fetch fails.
 *
 * ─── GOOGLE SHEET SETUP ────────────────────────────────────────────────────
 *
 * The sheet must have two tabs named exactly:
 *   - "novo_units"
 *   - "edge_cases"
 *
 * novo_units columns (row 1 = headers):
 *   model | min_bedrooms | max_bedrooms | tero_qty | phosclear_qty
 *
 * edge_cases columns (row 1 = headers):
 *   min_bedrooms | max_bedrooms | tero_qty | phosclear_qty
 *   (these rows override the main table for specific bedroom ranges)
 *
 * To get the CSV URL for each tab:
 *   1. File → Share → Publish to web
 *   2. Choose the tab name, select "Comma-separated values (.csv)"
 *   3. Click Publish and copy the URL
 *
 */

import { useState, useEffect } from 'react';

// ─── CSV parser ─────────────────────────────────────────────────────────────

function parseCsv(text) {
  const [headerLine, ...rows] = text.trim().split('\n');
  const headers = headerLine.split(',').map(h => h.trim());
  return rows
    .map(row => {
      const values = row.split(',').map(v => v.trim());
      return Object.fromEntries(headers.map((h, i) => [h, values[i]]));
    })
    .filter(row => Object.values(row).some(v => v !== ''));
}

// ─── Transform CSV rows into the config shape the app expects ───────────────

function buildConfigFromSheetData(novoUnitRows, edgeCaseRows) {
  // Map each row to a base_model entry
  const base_models = novoUnitRows.map(row => ({
    name:          row.model,
    max_bedrooms:  parseInt(row.max_bedrooms, 10),
    tero_qty:      parseInt(row.tero_qty, 10),
    phosclear_qty: parseInt(row.phosclear_qty, 10),
  }));

  // edge_cases: bedroom ranges where tero/phosclear quantities differ from
  // what the main table would give (e.g. 47-48 bedrooms → 9 Tero, 5 PhosClear)
  const edge_cases = edgeCaseRows.map(row => ({
    min_bedrooms:  parseInt(row.min_bedrooms, 10),
    max_bedrooms:  parseInt(row.max_bedrooms, 10),
    tero_qty:      parseInt(row.tero_qty, 10),
    phosclear_qty: parseInt(row.phosclear_qty, 10),
  }));

  return { base_models, edge_cases };
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useCalcConfig() {
  const wpConfig = window.TricelConfig?.calcConfig ?? null;

  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch('https://unsubscribe-test.tricel.eu/wwt-config');
        if (!res.ok) throw new Error('Config fetch failed');

        const { novoUnits, edgeCases } = await res.json();

        if (!novoUnits?.length) throw new Error('novo_units empty');

        const base_models = novoUnits.map(row => ({
          name:          row.model,
          image_url:     row.image_url ?? '',
          max_bedrooms:  parseInt(row.max_bedrooms, 10),
          tero_qty:      parseInt(row.tero_qty, 10),
          phosclear_qty: parseInt(row.phosclear_qty, 10),
        }));

        const edge_cases = edgeCases.map(row => ({
          min_bedrooms:  parseInt(row.min_bedrooms, 10),
          max_bedrooms:  parseInt(row.max_bedrooms, 10),
          tero_qty:      parseInt(row.tero_qty, 10),
          phosclear_qty: parseInt(row.phosclear_qty, 10),
        }));

        setConfig({ ...(wpConfig ?? {}), base_models, edge_cases });
      } catch (err) {
        console.warn('[WWT Calculator] Config fetch failed, using fallback:', err.message);
        setError(err.message);
        setConfig(wpConfig ?? DEFAULT_CONFIG);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  return { config, loading, error };
}

// ─── Selector functions ──────────────────────────────────────────────────────
// These replace the hardcoded logic in getBaseModel / buildRecommendation.
// Import and use these in App.js instead of the old functions.

/**
 * Returns the base model name for a given bedroom count.
 * config comes from useCalcConfig().config
 */
export function getBaseModel(bedrooms, config) {
  if (!config?.base_models) return null;
  // base_models should be sorted ascending by max_bedrooms
  for (const model of config.base_models) {
    if (bedrooms <= model.max_bedrooms) return model.name;
  }
  // Above the last threshold → commercial/contact sales
  return 'COMMERCIAL';
}

/**
 * Returns { teroQty, phosclearQty } for a given bedroom count,
 * applying edge case overrides where defined.
 */
export function getAddonQtys(bedrooms, config) {
  if (!config?.base_models) return { teroQty: 0, phosclearQty: 0 };

  // Check edge cases first
  if (config.edge_cases) {
    for (const ec of config.edge_cases) {
      if (bedrooms >= ec.min_bedrooms && bedrooms <= ec.max_bedrooms) {
        return { teroQty: ec.tero_qty, phosclearQty: ec.phosclear_qty };
      }
    }
  }

  // Fall back to main table
  for (const model of config.base_models) {
    if (bedrooms <= model.max_bedrooms) {
      return { teroQty: model.tero_qty, phosclearQty: model.phosclear_qty };
    }
  }

  return { teroQty: 0, phosclearQty: 0 };
}

// ─── Hardcoded default (last resort if sheet and WP both unavailable) ────────

const DEFAULT_CONFIG = {
  base_models: [
    { name: 'Tricel Novo UK6', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_1-6.png', max_bedrooms: 4,  tero_qty: 1, phosclear_qty: 1 },
    { name: 'Tricel Novo UK8', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_7-9.png', max_bedrooms: 6,  tero_qty: 2, phosclear_qty: 1 },
    { name: 'Tricel Novo UK10', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_10-11.png', max_bedrooms: 8,  tero_qty: 2, phosclear_qty: 1 },
    { name: 'Tricel Novo UK12', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_12-14.png', max_bedrooms: 10, tero_qty: 2, phosclear_qty: 1 },
    { name: 'Tricel Novo UK18', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_15-17.png', max_bedrooms: 16, tero_qty: 3, phosclear_qty: 2 },
    { name: 'Tricel Novo UK24', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_20.png', max_bedrooms: 22, tero_qty: 4, phosclear_qty: 2 },
    { name: 'Tricel Novo UK30', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_20.png', max_bedrooms: 28, tero_qty: 5, phosclear_qty: 3 },
    { name: 'Tricel Novo UK36', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_20.png', max_bedrooms: 34, tero_qty: 6, phosclear_qty: 3 },
    { name: 'Tricel Novo UK42', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_20.png', max_bedrooms: 40, tero_qty: 7, phosclear_qty: 4 },
    { name: 'Tricel Novo UK50', image_url: 'https://tricel.co.uk/sewage-treatment/wp-content/uploads/2026/06/Novo_Sizes_20.png', max_bedrooms: 48, tero_qty: 8, phosclear_qty: 4 },
  ],
  edge_cases: [
    { min_bedrooms: 47, max_bedrooms: 48, tero_qty: 9, phosclear_qty: 5 },
  ],
};